import { JobItemDiscountType, Maybe } from '../../common/types'
import {
  calculateNetSum,
  calculateVat,
  calculateDiscountNetAmount,
  calculateDiscountGrossAmount,
  anyHasUndefinedPrice,
} from './priceCalculations'
import { JobItemInput } from './types'

interface Params {
  jobItems: JobItemInput[]
  itemsIncludeVat: boolean
  discountItem: Maybe<JobItemInput>
}

export interface OrderTotals {
  netAmountWithDiscountItem: number
  vatAmountWithDiscountItem: number
  grossAmountWithDiscountItem: number
  discountNetTotal: number
  discountGrossTotal: number
  hasUndefinedPrice: boolean
}

export const calculateOrderTotals = ({
  itemsIncludeVat,
  jobItems,
  discountItem,
}: Params): OrderTotals | null => {
  if (!jobItems) return null

  const netAmountAccumulator = (vatIncluded: boolean) => (accumulator: number, item: JobItemInput) =>
    accumulator + calculateNetSum(item, vatIncluded)
  const vatAccumulator = (vatIncluded: boolean) => (accumulator: number, item: JobItemInput) =>
    accumulator + calculateVat(item, vatIncluded)
  const discountNetAccumulator = (vatIncluded: boolean) => (accumulator: number, item: JobItemInput) =>
    accumulator + calculateDiscountNetAmount(item, vatIncluded)
  const discountGrossAccumulator = (vatIncluded: boolean) => (accumulator: number, item: JobItemInput) =>
    accumulator + calculateDiscountGrossAmount(item, vatIncluded)

  const calculateNetAmount = netAmountAccumulator(itemsIncludeVat)
  const calculateVatAmount = vatAccumulator(itemsIncludeVat)
  const calculateDiscountNet = discountNetAccumulator(itemsIncludeVat)
  const calculateDiscountGross = discountGrossAccumulator(itemsIncludeVat)

  const netAmount = jobItems.reduce(calculateNetAmount, 0)
  const vatAmount = jobItems.reduce(calculateVatAmount, 0)
  const grossAmount = netAmount + vatAmount

  const discountNetAmount = jobItems.reduce(calculateDiscountNet, 0)
  const discountGrossAmount = jobItems.reduce(calculateDiscountGross, 0)

  const discountItemNetAmount = !discountItem
    ? 0
    : discountItem.discountType === JobItemDiscountType.percentage
    ? (netAmount * Math.abs(discountItem.unitPrice)) / 100
    : Math.abs(discountItem.unitPrice) / (1 + discountItem.vat / 100)

  const discountItemGrossAmount = !discountItem
    ? 0
    : discountItem.discountType === JobItemDiscountType.percentage
    ? (grossAmount * Math.abs(discountItem.unitPrice)) / 100
    : Math.abs(discountItem.unitPrice)

  const discountItemVatAmount = discountItemGrossAmount - discountItemNetAmount

  return {
    discountNetTotal: -(discountNetAmount + discountItemNetAmount),
    discountGrossTotal: -(discountGrossAmount + discountItemGrossAmount),
    netAmountWithDiscountItem: netAmount - discountItemNetAmount,
    grossAmountWithDiscountItem: grossAmount - discountItemGrossAmount,
    vatAmountWithDiscountItem: vatAmount - discountItemVatAmount,
    hasUndefinedPrice: anyHasUndefinedPrice(jobItems),
  }
}

export const getItemsTotal = (params: Params): number | null => {
  const totals = calculateOrderTotals(params)
  if (!totals) return null
  return totals.grossAmountWithDiscountItem
}
